import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { WarningCircleIcon } from "@phosphor-icons/react"
import { useRef } from "react"

import {
  PILL_CLASS,
  PILL_TRACK_CLASS,
  PILL_TRIGGER_CLASS,
  useActivePillRect,
} from "~/components/reusable/Tabs"
import { cn } from "~/utils/helpers"

export type SegmentedControlOption<T extends string> = {
  value: T
  label: string
  warning?: string
}

export type SegmentedControlProps<T extends string> = {
  value: T
  options: SegmentedControlOption<T>[]
  onValueChange: (value: T) => void
  "aria-label": string
  size?: "base" | "sm"
  className?: string
}

const ACTIVE_ITEM = '[role="radio"][data-state="checked"]'

/**
 * One visible choice among 2–5 short options that switches what a page or section shows; a
 * sliding pill marks the selected one. Radio semantics: arrow keys move the selection.
 * Canonical intent: {@link ../design-system/intents.ts SEGMENTED_CONTROL_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export function SegmentedControl<T extends string>({
  value,
  options,
  onValueChange,
  "aria-label": ariaLabel,
  size = "base",
  className,
}: SegmentedControlProps<T>) {
  const trackRef = useRef<HTMLDivElement>(null)
  const pill = useActivePillRect(trackRef, true, ACTIVE_ITEM)

  return (
    <RadioGroupPrimitive.Root
      ref={trackRef}
      value={value}
      onValueChange={(next) => onValueChange(next as T)}
      orientation="horizontal"
      aria-label={ariaLabel}
      // w-fit: a flex column would otherwise stretch the track past its segments.
      className={cn(PILL_TRACK_CLASS, "w-fit", size === "base" ? "h-8" : "h-7", className)}
    >
      {pill && (
        <span
          aria-hidden
          className={PILL_CLASS}
          style={{ width: pill.width, height: pill.height, translate: `${pill.x}px ${pill.y}px` }}
        />
      )}
      {options.map((option) => (
        <RadioGroupPrimitive.Item
          key={option.value}
          value={option.value}
          className={cn(
            PILL_TRIGGER_CLASS,
            "gap-1 data-[state=checked]:text-fg-base",
            size === "base" ? "h-7 px-1.5 py-1" : "h-6 px-2 py-1.5",
          )}
        >
          {option.label}
          {option.warning && (
            <>
              <WarningCircleIcon
                aria-hidden
                weight="fill"
                className="size-3.5 shrink-0 text-fg-warning-icon"
              />
              <span className="sr-only">{option.warning}</span>
            </>
          )}
        </RadioGroupPrimitive.Item>
      ))}
    </RadioGroupPrimitive.Root>
  )
}
